import {create} from "zustand";

import {IDLE_ESCALATION_MS, shouldDeferForBusy} from "@/src/modules/core/lib/compliance-busy";
import {loadActiveResumableFormDraft} from "@/src/modules/core/lib/form-draft";

type ComplianceBusyState = {
  /** Number of open, dirty sheets (supports nested/overlapping sheets). */
  busyCount: number;
  /** Timestamp of the last meaningful input while busy; null when not busy. */
  lastInputAt: number | null;
  /** Whether gates are currently deferred behind the sticky bar. Derived, kept in state so the single timer can flip it. */
  deferred: boolean;
  /** User chose "Review now" — keep gates visible until they resume editing. */
  userEscalatedGates: boolean;
};

type ComplianceBusyActions = {
  enterBusy: () => void;
  exitBusy: () => void;
  markInput: () => void;
  /** User chose "Review now" from the sticky bar: surface the gate over the sheet immediately. */
  escalateNow: () => void;
  /** Re-read localStorage draft state (cold start / save / clear). */
  syncFormDraftDeferral: () => void;
  /** Reset after all blocking gates clear so the next obligation can defer again. */
  clearUserEscalation: () => void;
};

let idleTimer: ReturnType<typeof setTimeout> | null = null;

export const useComplianceBusyStore = create<ComplianceBusyState & ComplianceBusyActions>(
  (set, get) => {
    function clearIdleTimer() {
      if (idleTimer != null) {
        clearTimeout(idleTimer);
        idleTimer = null;
      }
    }

    function recompute() {
      clearIdleTimer();
      const state = get();
      if (state.userEscalatedGates) {
        set({deferred: false});
        return;
      }
      const {busyCount, lastInputAt} = state;
      const isBusy = busyCount > 0;
      const hasResumableDraft = loadActiveResumableFormDraft() != null;
      // Draft defers only while the sheet is not mounted yet (cold-start / resume gap).
      const deferForDraft = hasResumableDraft && !isBusy;
      const deferredFromBusy = shouldDeferForBusy({isBusy, lastInputAt, now: Date.now()});
      const deferred = deferForDraft || deferredFromBusy;
      set({deferred});
      if (isBusy && lastInputAt != null) {
        const delay = lastInputAt + IDLE_ESCALATION_MS - Date.now();
        idleTimer = setTimeout(() => {
          idleTimer = null;
          recompute();
        }, Math.max(0, delay));
      }
    }

    return {
      busyCount: 0,
      lastInputAt: null,
      deferred: false,
      userEscalatedGates: false,

      enterBusy: () => {
        set((state) => ({
          busyCount: state.busyCount + 1,
          lastInputAt: state.lastInputAt ?? Date.now(),
        }));
        recompute();
      },

      exitBusy: () => {
        set((state) => {
          const busyCount = Math.max(0, state.busyCount - 1);
          return {busyCount, lastInputAt: busyCount === 0 ? null : state.lastInputAt};
        });
        recompute();
      },

      markInput: () => {
        if (get().busyCount === 0) return;
        set({lastInputAt: Date.now(), userEscalatedGates: false});
        recompute();
      },

      escalateNow: () => {
        if (get().busyCount === 0 && loadActiveResumableFormDraft() == null) return;
        clearIdleTimer();
        set({deferred: false, userEscalatedGates: true});
      },

      syncFormDraftDeferral: () => {
        recompute();
      },

      clearUserEscalation: () => {
        if (!get().userEscalatedGates) {
          return;
        }
        set({userEscalatedGates: false});
        recompute();
      },
    };
  },
);

export function useIsComplianceBusy(): boolean {
  return useComplianceBusyStore((state) => state.busyCount > 0);
}

export function useEscalateCompliance() {
  return useComplianceBusyStore((state) => state.escalateNow);
}

if (typeof window !== "undefined") {
  useComplianceBusyStore.getState().syncFormDraftDeferral();
}
